import { z } from 'zod';

/**
 * RBAC roles. `chair_renter` is a *tenant boundary*, not merely a permission set:
 * a renter's sealed client book is invisible to everyone else by RLS architecture.
 * See FOUNDATION.md and ADR-0005.
 */
export const Role = z.enum(['owner', 'manager', 'stylist', 'chair_renter', 'receptionist']);
export type Role = z.infer<typeof Role>;

/** Roles that may see the whole floor (every appointment slot) for diary management. */
export const FLOOR_MANAGER_ROLES: ReadonlySet<Role> = new Set(['owner', 'manager']);

/**
 * Salon-level client-book model, chosen at setup, changeable later, overridable per person.
 * The mode is realised in the *data* (whether client rows carry a renter_id), not in the
 * RLS policy text — the policy is identical in all three modes (ADR-0005).
 */
export const ClientBookMode = z.enum(['shared', 'sealed', 'mixed']);
export type ClientBookMode = z.infer<typeof ClientBookMode>;

/** How a staff member is engaged. Chair renters own a sealed book in sealed/mixed salons. */
export const StaffEngagement = z.enum(['employed', 'chair_renter']);
export type StaffEngagement = z.infer<typeof StaffEngagement>;

/** Per-person book override used in `mixed` salons. `inherit` follows the salon default. */
export const BookOverride = z.enum(['inherit', 'shared', 'sealed']);
export type BookOverride = z.infer<typeof BookOverride>;

export const AppointmentStatus = z.enum([
  'booked',
  'confirmed',
  'in_chair',
  'completed',
  'no_show',
  'cancelled',
]);
export type AppointmentStatus = z.infer<typeof AppointmentStatus>;

/** Source channel an appointment originated from. */
export const AppointmentSource = z.enum(['salon', 'online', 'queue', 'walk_in', 'api']);
export type AppointmentSource = z.infer<typeof AppointmentSource>;

/**
 * Walk-in queue ticket lifecycle. ETA + position are computed, not stored verbatim.
 * `called` = staff invited them to the chair; `in_chair` = service started.
 */
export const QueueTicketStatus = z.enum([
  'waiting',
  'called',
  'in_chair',
  'completed',
  'no_show',
  'left',
]);
export type QueueTicketStatus = z.infer<typeof QueueTicketStatus>;

/** Client comms channels. email + push are unmetered/free; sms + whatsapp draw on allowance. */
export const CommsChannel = z.enum(['email', 'push', 'sms', 'whatsapp']);
export type CommsChannel = z.infer<typeof CommsChannel>;

export const METERED_CHANNELS: ReadonlySet<z.infer<typeof CommsChannel>> = new Set([
  'sms',
  'whatsapp',
]);

/** Salon-chosen behaviour when a metered allowance is exhausted (design screen S2). */
export const AtCapPolicy = z.enum(['pause', 'ask', 'auto_top_up']);
export type AtCapPolicy = z.infer<typeof AtCapPolicy>;
